import { useCallback, useEffect, useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import { ApiError, ClientContextProvider, usePortal, type ClientContextValue, type ClientDetail } from "@app/portal-sdk";
import { Alert, EmptyState } from "@app/ui-kit";
import { LoadingScreen } from "../components/LoadingScreen";
import { useShell } from "../components/ShellContext";
import { useApiQuery } from "../lib/useApiQuery";

/**
 * Layout for /c/:slug. Loads the client, checks access, and provides
 * ClientContext to every nested page and application.
 */
export const ClientPortalLayout = () => {
  const { slug = "" } = useParams();
  const { api, isBkdpAdmin, memberships } = usePortal();
  const shell = useShell();

  const query = useApiQuery<ClientDetail>(async () => {
    try {
      return await api.request<ClientDetail>(`/clients/${slug}`);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
        throw new ApiError(err.status, err.status === 404 ? "not_found" : "forbidden");
      }
      throw err;
    }
  }, [api, slug]);

  const client = query.data;
  const role = useMemo(
    () => (client ? memberships.find((m) => m.client.id === client.id)?.role ?? null : null),
    [client, memberships]
  );
  const canManage = isBkdpAdmin || role === "client_admin";

  useEffect(() => {
    shell.setClient(client ?? null, canManage);
    return () => shell.setClient(null, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, canManage]);

  const refreshClient = useCallback(() => query.reload(), [query.reload]);

  if (query.loading && !client) return <LoadingScreen />;

  if (query.error === "not_found") {
    return <EmptyState eyebrow="Not Found" title="There is no client portal at this address" />;
  }
  if (query.error === "forbidden") {
    return (
      <EmptyState
        eyebrow="No Access"
        title="You don't have access to this client"
        description="If you believe you should, contact the person who invited you."
      />
    );
  }
  if (query.error && !client) return <Alert severity="error">{query.error}</Alert>;
  if (!client) return null;

  const value: ClientContextValue = { client, role, isBkdpAdmin, canManage, refreshClient };

  return (
    <ClientContextProvider value={value}>
      <Outlet />
    </ClientContextProvider>
  );
};
